import React, { useState, useMemo } from "react";
import {
  ExclamationTriangleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import { UserSegmentEnum } from "../../../types";
import {
  useGetErrorReports,
  useGetErrorReportsByStatusCode,
} from "../../../api/queries";
import Input from "../../../componentLibrary/Input";
import Spinner from "../../../componentLibrary/Spinner";
import Button from "../../../componentLibrary/Button";
import MultiSelectDropdown, {
  MultiSelectOptionI,
} from "../../../componentLibrary/MultiSelectDropdown";
import BarChartWidget from "./reusable/BarChartWidget";
import ErrorReportCard from "./ErrorReportCard";

interface ErrorReportWidgetProps {
  selectedSegments: UserSegmentEnum[];
  startDate?: string;
  endDate?: string;
}

const ErrorReportWidget: React.FC<ErrorReportWidgetProps> = ({
  selectedSegments,
  startDate,
  endDate,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedStatusCodes, setSelectedStatusCodes] = useState<
    MultiSelectOptionI<number>[]
  >([]);
  const [includeResolved, setIncludeResolved] = useState(false);
  const itemsPerPage = 20;

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  React.useEffect(() => {
    setCurrentPage(1);
  }, [
    startDate,
    endDate,
    debouncedSearch,
    selectedStatusCodes,
    includeResolved,
    selectedSegments,
  ]);

  const statusCodesFilter =
    selectedStatusCodes.length > 0
      ? selectedStatusCodes.map((opt) => opt.value)
      : undefined;

  const {
    data: errorReportsResponse,
    isLoading,
    error,
  } = useGetErrorReports(
    selectedSegments,
    debouncedSearch,
    startDate,
    endDate,
    statusCodesFilter,
    includeResolved,
    currentPage,
    itemsPerPage,
  );

  const errorReports = errorReportsResponse?.data || [];
  const pagination = errorReportsResponse?.pagination;
  const statusCodeCounts = errorReportsResponse?.statusCodeCounts || {};

  const errorCount = pagination?.total || 0;

  const { data: statusCodeChartData = [] } = useGetErrorReportsByStatusCode(
    selectedSegments,
    debouncedSearch,
    startDate,
    endDate,
    statusCodesFilter,
    includeResolved,
  );

  const statusCodeOptions: MultiSelectOptionI<number>[] = useMemo(() => {
    return Object.entries(statusCodeCounts).map(([code, count]) => ({
      value: parseInt(code, 10),
      label: `${code} (${count})`,
    }));
  }, [statusCodeCounts]);

  if (error) {
    return (
      <div className="p-5 bg-red-50 rounded-lg">
        <p className="text-red-600">
          Failed to load error reports: {error.message}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {statusCodeChartData.length > 0 && (
        <BarChartWidget
          data={statusCodeChartData}
          title="HTTP Status Codes"
          metric="errors"
        />
      )}

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Error Reports</h2>
          <p className="text-sm text-gray-600 mt-1">
            {errorCount} total reports
          </p>
        </div>
      </div>

      <Input
        type="text"
        placeholder="Search error messages..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="w-full"
      />

      <div className="flex flex-col md:flex-row gap-4">
        {statusCodeOptions.length > 0 && (
          <div className="flex-1">
            <MultiSelectDropdown
              label="Filter by HTTP Status Code"
              options={statusCodeOptions}
              value={selectedStatusCodes}
              onChange={setSelectedStatusCodes}
              placeholder="All status codes"
            />
          </div>
        )}

        <div className="flex items-end">
          <label className="flex items-center gap-2 cursor-pointer bg-white border border-gray-300 rounded-md px-4 py-2 hover:bg-gray-50 transition-colors">
            <input
              type="checkbox"
              checked={includeResolved}
              onChange={(e) => setIncludeResolved(e.target.checked)}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">
              Include Resolved Errors
            </span>
          </label>
        </div>
      </div>

      {(startDate ||
        endDate ||
        selectedStatusCodes.length > 0 ||
        searchTerm ||
        includeResolved) && (
        <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded-md space-y-1">
          <span className="font-semibold block">Active Filters:</span>
          {(startDate || endDate) && (
            <div>
              <span className="font-medium">Date:</span>
              {startDate && (
                <span className="ml-2">
                  From: {new Date(startDate).toLocaleDateString()}
                </span>
              )}
              {endDate && (
                <span className="ml-2">
                  To: {new Date(endDate).toLocaleDateString()}
                </span>
              )}
            </div>
          )}
          {selectedStatusCodes.length > 0 && (
            <div>
              <span className="font-medium">Status Codes:</span>
              <span className="ml-2">
                {selectedStatusCodes.map((sc) => sc.value).join(", ")}
              </span>
            </div>
          )}
          {searchTerm && (
            <div>
              <span className="font-medium">Search:</span>
              <span className="ml-2">{searchTerm}</span>
            </div>
          )}
          {includeResolved && (
            <div>
              <span className="font-medium">Including resolved errors</span>
            </div>
          )}
        </div>
      )}

      {isLoading && (
        <div className="flex justify-center items-center py-12">
          <Spinner />
        </div>
      )}

      {!isLoading && errorReports && (
        <>
          {errorReports.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <ExclamationTriangleIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">
                {searchTerm ||
                startDate ||
                endDate ||
                selectedStatusCodes.length > 0
                  ? "No error reports match your filters"
                  : "No error reports found"}
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4">
                {errorReports.map((errorReport) => (
                  <ErrorReportCard key={errorReport._id} error={errorReport} />
                ))}
              </div>

              {pagination && pagination.totalPages > 1 && (
                <div className="flex items-center justify-between mt-6 bg-white p-4 rounded-lg shadow-sm border">
                  <div className="text-sm text-gray-600">
                    Showing{" "}
                    <span className="font-semibold">
                      {(pagination.page - 1) * pagination.limit + 1}
                    </span>{" "}
                    to{" "}
                    <span className="font-semibold">
                      {Math.min(
                        pagination.page * pagination.limit,
                        pagination.total,
                      )}
                    </span>{" "}
                    of <span className="font-semibold">{pagination.total}</span>{" "}
                    results
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      variant="alternative"
                      size="sm"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={pagination.page === 1}
                    >
                      <ChevronLeftIcon className="w-5 h-5" />
                    </Button>

                    <div className="flex items-center gap-1">
                      {Array.from(
                        { length: pagination.totalPages },
                        (_, i) => i + 1,
                      )
                        .filter((pageNum) => {
                          return (
                            pageNum === 1 ||
                            pageNum === pagination.totalPages ||
                            Math.abs(pageNum - pagination.page) <= 1
                          );
                        })
                        .map((pageNum, idx, arr) => (
                          <React.Fragment key={pageNum}>
                            {idx > 0 && arr[idx - 1] !== pageNum - 1 && (
                              <span className="px-2 text-gray-400">...</span>
                            )}
                            <Button
                              variant={
                                pageNum === pagination.page
                                  ? "primary"
                                  : "alternative"
                              }
                              size="sm"
                              onClick={() => setCurrentPage(pageNum)}
                              className="min-w-[2.5rem]"
                            >
                              {pageNum}
                            </Button>
                          </React.Fragment>
                        ))}
                    </div>
                    <Button
                      variant="alternative"
                      size="sm"
                      onClick={() =>
                        setCurrentPage((p) =>
                          Math.min(pagination.totalPages, p + 1),
                        )
                      }
                      disabled={pagination.page === pagination.totalPages}
                    >
                      <ChevronRightIcon className="w-5 h-5" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default ErrorReportWidget;
